import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Server, Eye, EyeOff, Send, Loader2, CheckCircle, XCircle, Info, ChevronDown, ChevronUp, Mail, X } from 'lucide-react';
import apiClient from '../services/apiClient';
import { toast } from 'react-hot-toast';

export interface SMTPConfig {
  smtp_email: string;
  smtp_senha: string;
  smtp_servidor: string;
  smtp_porta: number;
  smtp_tls: boolean;
}

interface SMTPConfigSectionProps {
  config: SMTPConfig;
  onChange: (config: SMTPConfig) => void;
  hasPassword?: boolean;
  userId?: number; // Se fornecido, testa SMTP para este usuário específico
  collapsible?: boolean;
  defaultExpanded?: boolean;
  showTestButton?: boolean;
  title?: string;
  compact?: boolean;
}

const SMTPConfigSection: React.FC<SMTPConfigSectionProps> = ({
  config,
  onChange,
  hasPassword = false,
  userId,
  collapsible = true,
  defaultExpanded = false,
  showTestButton = true,
  title = 'Configuração SMTP',
  compact = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showPassword, setShowPassword] = useState(false);
  const [isTestingSMTP, setIsTestingSMTP] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; details?: string } | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  // Modal "Para qual email enviar o teste?" — permite escolher destinatario
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  // Loading do fetch da senha quando clica no olho (so quando hasPassword=true)
  const [revealingPassword, setRevealingPassword] = useState(false);

  const handleInputChange = (field: keyof SMTPConfig, value: string | number | boolean) => {
    onChange({ ...config, [field]: value });
    setTestResult(null);
  };

  // Validacao basica de email (regex simples cobre maioria dos casos reais)
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  // Toggle do botao 'olho': se vai exibir e tem senha salva mas o input esta
  // vazio, busca no backend pra exibir o valor real. Caso contrario, so alterna
  // o tipo do input.
  const handleTogglePassword = async () => {
    const goingToShow = !showPassword;
    if (goingToShow && hasPassword && !config.smtp_senha && userId) {
      try {
        setRevealingPassword(true);
        // Endpoint mudou de GET para POST (security: GET vaza em logs/Referer/history)
        const response = await apiClient.post(
          `/users/${userId}/smtp/password/reveal`,
          {},
          { headers: { 'Cache-Control': 'no-store' } },
        );
        const password = response.data?.password || '';
        if (password) {
          onChange({ ...config, smtp_senha: password });
        }
      } catch (error: any) {
        console.error('Error revealing password:', error);
        const errorMessage = error.response?.data?.detail || 'Nao foi possivel revelar a senha';
        toast.error(errorMessage);
        setRevealingPassword(false);
        return;
      } finally {
        setRevealingPassword(false);
      }
    }
    setShowPassword(goingToShow);
  };

  const openRecipientModal = () => {
    if (!config.smtp_email) {
      toast.error('Configure o email SMTP antes de testar');
      return;
    }
    if (!hasPassword && !config.smtp_senha) {
      toast.error('Configure a senha SMTP antes de testar');
      return;
    }
    // Default: o proprio email SMTP. Usuario pode trocar pra qualquer outro
    // (ex: otavio.pereira@gmail.com pra confirmar entrega externa).
    setTestRecipient(config.smtp_email || '');
    setTestResult(null);
    setShowRecipientModal(true);
  };

  const handleTestSMTP = async (recipient: string) => {
    if (!isValidEmail(recipient)) {
      toast.error('Email destinatario invalido');
      return;
    }

    setIsTestingSMTP(true);
    setTestResult(null);
    setShowRecipientModal(false);

    try {
      // Se userId fornecido, testa SMTP do usuario; senao SMTP central
      const endpoint = userId ? `/users/${userId}/smtp/test` : '/config/smtp/test';
      const payload: any = userId ? {
        smtp_email: config.smtp_email,
        smtp_password: config.smtp_senha,
        smtp_servidor: config.smtp_servidor,
        smtp_porta: config.smtp_porta,
        smtp_tls: config.smtp_tls,
        test_recipient: recipient,
      } : {
        test_recipient: recipient,
      };

      const response = await apiClient.post(endpoint, payload);
      const data = response.data;

      setTestResult({
        success: data.success,
        message: data.message,
        details: data.details
      });

      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error: any) {
      console.error('Error testing SMTP:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao testar SMTP';
      setTestResult({
        success: false,
        message: 'Erro ao testar conexão',
        details: errorMessage
      });
      toast.error(errorMessage);
    } finally {
      setIsTestingSMTP(false);
    }
  };

  const content = (
    <div className={`space-y-4 ${compact ? 'space-y-3' : 'space-y-6'}`}>
      {/* Test Result Alert */}
      {testResult && (
        <div className={`p-3 rounded-lg flex items-start gap-3 ${
          testResult.success
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
        }`}>
          {testResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <p className={`font-medium text-sm ${testResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
              {testResult.message}
            </p>
            {testResult.details && (
              <p className={`text-xs mt-1 ${testResult.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                {testResult.details}
              </p>
            )}
          </div>
        </div>
      )}

      {/* SMTP Form Grid */}
      <div className={`grid grid-cols-1 ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2'} gap-4`}>
        {/* Email SMTP */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Email SMTP <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={config.smtp_email}
            onChange={(e) => handleInputChange('smtp_email', e.target.value)}
            placeholder="sistema@empresa.com"
            className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Email para envio de notificações
          </p>
        </div>

        {/* Senha SMTP */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Senha / App Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={config.smtp_senha}
              onChange={(e) => handleInputChange('smtp_senha', e.target.value)}
              placeholder={hasPassword ? "••••••••••••••••" : "Senha do email"}
              className="w-full px-4 py-2 pr-12 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              type="button"
              onClick={handleTogglePassword}
              disabled={revealingPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
              title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {revealingPassword ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {hasPassword && !config.smtp_senha ? (
            <div className="flex items-center gap-1.5 mt-1">
              <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                Senha salva. Clique no <Eye className="inline w-3 h-3" /> para ver, ou deixe em branco para manter.
              </span>
            </div>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {hasPassword ? 'Deixe em branco para manter a senha atual' : 'Senha ou App Password'}
            </p>
          )}
        </div>

        {/* Servidor SMTP */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Servidor SMTP
          </label>
          <input
            type="text"
            value={config.smtp_servidor}
            onChange={(e) => handleInputChange('smtp_servidor', e.target.value)}
            placeholder="smtp.gmail.com"
            className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        {/* Porta SMTP */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Porta SMTP
          </label>
          <input
            type="number"
            value={config.smtp_porta}
            onChange={(e) => handleInputChange('smtp_porta', parseInt(e.target.value) || 587)}
            placeholder="587"
            className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* TLS Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">Usar TLS (Conexão Segura)</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Recomendado para servidores modernos</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={config.smtp_tls}
            onChange={(e) => handleInputChange('smtp_tls', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      {/* Action Buttons */}
      {showTestButton && (
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="button"
            onClick={openRecipientModal}
            disabled={isTestingSMTP || (!hasPassword && !config.smtp_senha)}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm"
          >
            {isTestingSMTP ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Testando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Testar Envio
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setShowInfoModal(true)}
            className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 transition"
            title="Dicas de Configuração SMTP"
          >
            <Info size={20} />
          </button>
        </div>
      )}

      {/* Info Modal — rendered via Portal to escape parent overflow/z-index stacking */}
      {showInfoModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4" onClick={() => setShowInfoModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Info className="text-white" size={24} />
                <h3 className="text-lg font-semibold text-white">Dicas de Configuração SMTP</h3>
              </div>
              <button
                onClick={() => setShowInfoModal(false)}
                className="text-white hover:bg-white/20 rounded-lg p-1 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border-2 border-blue-200 dark:border-blue-700">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">📧 Configuração Geral</h4>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li><strong>Email SMTP:</strong> O endereço de email que será usado para enviar as notificações.</li>
                  <li><strong>Senha:</strong> A senha do email ou "App Password" fornecida pelo seu provedor.</li>
                  <li><strong>Servidor SMTP:</strong> O endereço do servidor de email do seu provedor.</li>
                  <li><strong>Porta:</strong> A porta de conexão (geralmente 587 para TLS ou 465 para SSL).</li>
                  <li><strong>TLS:</strong> Conexão segura - recomendado manter ativado.</li>
                </ul>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">🌐 Configurações por Provedor</h4>
                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3">
                  <div className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                    <strong>Gmail:</strong> smtp.gmail.com | Porta: 587 | TLS: Sim
                    <p className="text-xs text-gray-500 mt-1">Requer "Senha de App" (16 caracteres) - Acesse: Conta Google → Segurança → Senhas de app</p>
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                    <strong>Outlook/Hotmail:</strong> smtp.office365.com | Porta: 587 | TLS: Sim
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                    <strong>Yahoo:</strong> smtp.mail.yahoo.com | Porta: 587 | TLS: Sim
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-700">
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">⚠️ Dica de Segurança</h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  Para maior segurança, use uma "Senha de Aplicativo" ao invés da senha principal do email.
                </p>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Recipient Modal — pergunta para qual email enviar o teste */}
      {showRecipientModal && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowRecipientModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Send className="text-white" size={24} />
                <h3 className="text-lg font-semibold text-white">Testar Envio de Email</h3>
              </div>
              <button
                onClick={() => setShowRecipientModal(false)}
                className="text-white hover:bg-white/20 rounded-lg p-1 transition"
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Para qual email deseja enviar o teste? Use seu email pessoal
                para validar que o SMTP está chegando ao destinatário externo.
              </p>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Email destinatário <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isValidEmail(testRecipient)) {
                      handleTestSMTP(testRecipient.trim());
                    }
                  }}
                  placeholder="seu.email@gmail.com"
                  autoFocus
                  className="w-full px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-green-500 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {testRecipient && !isValidEmail(testRecipient) && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    Email inválido
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Será enviado um email de teste de <strong>{config.smtp_email}</strong> para o endereço acima.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRecipientModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => handleTestSMTP(testRecipient.trim())}
                  disabled={!isValidEmail(testRecipient) || isTestingSMTP}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition flex items-center gap-2 text-sm"
                >
                  {isTestingSMTP ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar Teste
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );

  if (!collapsible) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <Server className="w-5 h-5" />
          {title}
        </h3>
        {content}
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left focus:outline-none"
      >
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Mail className="w-4 h-4" />
          {title}
        </h3>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {!isExpanded && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Configure email para envio de notificações
        </p>
      )}

      {isExpanded && (
        <div className="mt-4">
          {content}
        </div>
      )}
    </div>
  );
};

export default SMTPConfigSection;
